import { useTranslation } from "react-i18next";
import { Plus, Trash2, Star } from "lucide-react";
import { Button } from "@/components/ui/button";
import type { RegistrationForm, RegContact } from "./types";

const inputClass =
  "w-full h-10 px-3 border border-border bg-background text-sm focus:outline-none focus:border-primary transition-colors";
const labelClass = "text-micro block mb-2";

interface Props {
  form: RegistrationForm;
  update: (field: string, value: any) => void;
}

export default function RegKontakte({ form, update }: Props) {
  const { t } = useTranslation();

  const addContact = () => {
    const c: RegContact = {
      id: crypto.randomUUID(),
      name: "",
      role: "",
      email: "",
      phone: "",
      is_primary: form.contacts.length === 0,
    };
    update("contacts", [...form.contacts, c]);
  };

  const removeContact = (id: string) => {
    const remaining = form.contacts.filter((c) => c.id !== id);
    if (remaining.length > 0 && !remaining.some((c) => c.is_primary)) remaining[0].is_primary = true;
    update("contacts", remaining);
  };

  const updateContact = (id: string, field: keyof RegContact, value: any) => {
    update("contacts", form.contacts.map((c) => (c.id === id ? { ...c, [field]: value } : c)));
  };

  const setPrimary = (id: string) => {
    update("contacts", form.contacts.map((c) => ({ ...c, is_primary: c.id === id })));
  };

  return (
    <div className="space-y-8">
      <section className="border border-border bg-card p-6 space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-subheadline">{t("regForm.contacts")}</h3>
            <p className="text-caption mt-1">{t("regForm.contactsDesc")}</p>
          </div>
          <Button type="button" variant="quiet" size="sm" onClick={addContact}>
            <Plus className="w-4 h-4 mr-1" /> {t("regForm.addContact")}
          </Button>
        </div>

        {form.contacts.length === 0 && (
          <div className="p-8 border border-dashed border-border text-center">
            <p className="text-caption mb-4">{t("regForm.noContacts")}</p>
            <Button type="button" variant="quiet" size="sm" onClick={addContact}>
              <Plus className="w-4 h-4 mr-1" /> {t("regForm.createContact")}
            </Button>
          </div>
        )}

        {form.contacts.map((contact, idx) => (
          <div key={contact.id} className="border border-border p-5 space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <span className="text-micro">{t("regForm.contactN", { n: idx + 1 })}</span>
                {contact.is_primary && (
                  <span className="text-xs px-2 py-0.5 bg-accent/10 text-accent border border-accent/20">{t("regForm.primaryContact")}</span>
                )}
              </div>
              <div className="flex items-center gap-1">
                {!contact.is_primary && (
                  <button type="button" onClick={() => setPrimary(contact.id)} title={t("regForm.setPrimary")} className="p-1.5 text-muted-foreground hover:text-accent transition-colors">
                    <Star className="w-4 h-4" />
                  </button>
                )}
                <button type="button" onClick={() => removeContact(contact.id)} className="p-1.5 text-muted-foreground hover:text-destructive transition-colors">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className={labelClass}>{t("regForm.nameRequired")}</label>
                <input value={contact.name} onChange={(e) => updateContact(contact.id, "name", e.target.value)} className={inputClass} />
              </div>
              <div>
                <label className={labelClass}>{t("regForm.role")}</label>
                <input value={contact.role} onChange={(e) => updateContact(contact.id, "role", e.target.value)} placeholder={t("regForm.rolePlaceholderShort")} className={inputClass} />
              </div>
              <div>
                <label className={labelClass}>{t("regForm.emailRequired")}</label>
                <input type="email" value={contact.email} onChange={(e) => updateContact(contact.id, "email", e.target.value)} className={inputClass} />
              </div>
              <div>
                <label className={labelClass}>{t("common.phone")}</label>
                <input value={contact.phone} onChange={(e) => updateContact(contact.id, "phone", e.target.value)} placeholder="+49 ..." className={inputClass} />
              </div>
            </div>
          </div>
        ))}
      </section>
    </div>
  );
}
